import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useLayoutEffect,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import moment from 'moment';
import html2canvas from 'html2canvas';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import ReactGA from 'react-ga';

import { Select, Button, LoadingSpinner, Tabs } from '../../components/_shared';
import Quizzes from './components/Quizzes';
import Forum from '../Forum';
import {
  makeSelectCourses,
  makeSelectUserId,
  makeSelectUser,
  makeSelectCurrentCourse,
  makeSelectCurrentLesson,
  makeSelectCoursesLoading,
  makeSelectCurriculumIncompletedLessons,
  makeSelectCurrentLessonId,
  makeSelectIsIcscOrg,
  makeSelectIsBLNextOrg,
} from '../../store/selectors';
import {
  COURSE_SELECT,
  COURSE_NEXT_LESSON,
  COURSE_PREV_LESSON,
  COURSE_SELECT_LESSON,
  COURSE_COMPLETE_LESSON,
} from '../../store/actionTypes';
import { fetchCourses } from '../../store/actions/course';
import { getUserSettings, updateUserSettings } from '../../service/user';
import { createCertificate as certificateRequest } from '../../service/certificate';
import {
  replaceVideoScripts,
  notify,
  replaceOptInButton,
} from '../../utils/helper';
import { AppContext } from '../../providers/AppProvider';
import { ThemeContext } from '../../providers/ThemeProvider';
import checkIco from '../../assets/imgs/check.png';
import logo from '../../assets/imgs/rabbit.png';
import icscProgressIcon from '../../assets/imgs/icsc-progress.png';
import './description.scss';
import './icsc.scss';
import './mmi.scss';
import './bl-next.scss';
import 'react-circular-progressbar/dist/styles.css';

import {
  Container,
  SidebarContainer,
  TitleContainer,
  ContentSelector,
  ContentTitleContainer,
  LinkButton,
} from './Courses.styled';
import { isMMI } from '../../config';

const generateImage = async () => {
  const div = document.getElementById('drawContainer');
  window.scrollTo(0, 0);
  const canvas = await html2canvas(div, {
    letterRendering: 1,
    allowTaint: false,
    useCORS: true,
  });
  const myImg = canvas.toDataURL('image/png');
  return `data:${myImg}`;
};

const tabOptions = [
  { key: 'lesson', label: 'Lesson Content' },
  { key: 'q&a', label: 'Q&A' },
  // {
  //   key: 'resources',
  //   label: 'Resources',
  // },
];

const OPTIN_LESSON_TITLE = 'Career Connections Program';

const typeformLessonId = 'career-connections-program';

const AGENTE_CANDIDATE = [1, 2, 3, 4, 5, [6, 7, 8, 9, 10], 11];
const MMCC_CANDIDATE = [1, 2, 3, 4, 5, [6, 7, 8, 9, 10], 11, 12];
const BL_NEXT = [1, 2, 3, 4, 5];

const Courses = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { curriculumId, courseId, programId } = useParams();
  const {
    loading: loadingCurriculums,
    progress,
    program,
    coreCurriculum,
    endorsements,
    fetchProgram,
  } = useContext(AppContext);
  const { primaryColor } = useContext(ThemeContext);
  const courses = useSelector(makeSelectCourses());
  const userId = useSelector(makeSelectUserId());
  const user = useSelector(makeSelectUser());
  const loading = useSelector(makeSelectCoursesLoading());
  const currentCourse = useSelector(makeSelectCurrentCourse(courseId));
  const currentLesson = useSelector(makeSelectCurrentLesson());
  const currentLessonId = useSelector(makeSelectCurrentLessonId());
  const incompletedLessons = useSelector(
    makeSelectCurriculumIncompletedLessons()
  );
  const isIcsc = useSelector(makeSelectIsIcscOrg());
  const isBLNext = useSelector(makeSelectIsBLNextOrg());
  const [, setShowQuizzes] = useState(false);
  const [showForum, setShowForum] = useState(false);
  const [tab, setTab] = useState('lesson');
  const [settings, setSettings] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    localStorage.setItem(
      'resume_lesson_info',
      JSON.stringify({ curriculumId, programId, courseId })
    );
  }, [curriculumId, courseId, programId]);

  // Handles courseId param change in url
  useEffect(() => {
    if (courses.length) {
      let i = 0;

      for (; i < courses.length; i += 1) {
        if (courseId === courses[i].id) {
          break;
        }
      }

      dispatch({ type: COURSE_SELECT, id: i });
    }
  }, [courses, courseId]);

  const curriculum = useMemo(
    () =>
      [coreCurriculum, ...endorsements].find(
        (cur) => cur && cur.id === curriculumId
      ),
    [loadingCurriculums, coreCurriculum, endorsements, curriculumId]
  );

  const programType = useMemo(() => {
    const curTitle = curriculum?.title.toLowerCase();
    return curTitle?.includes('agent') ? 'AGENTE_CANDIDATE' : curTitle?.includes('mmcc') ? 'MMCC_CANDIDATE' : 'normal';
  }, [curriculum]);

  useEffect(() => {
    if (currentCourse && courseId !== currentCourse.id) {
      history.replace(
        `/programs/${programId}/${curriculumId}/courses/${currentCourse.id}`
      );
    }
  }, [currentCourse]);

  const percent = useMemo(() => {
    if (!curriculum) {
      return 0;
    }

    const totalPercent = curriculum.courses.reduce((p, c) => {
      const userCourse = progress.find(
        ({ course: coourse }) => coourse && coourse._id === c._id
      );

      if (userCourse) {
        const total =
          userCourse.course.lessons.length + userCourse.course.quizzes.length;
        const current =
          userCourse.completedLessons.length +
          userCourse.completedQuizzes.length;
        return p + Math.min(100, (current / total) * 100);
      } else {
        return p;
      }
    }, 0);
    return Math.floor(parseFloat(totalPercent) / curriculum.courses.length);
  }, [curriculum, progress]);

  const lessonContent = useMemo(() => {
    if (currentLesson && currentLesson.description) {
      return replaceVideoScripts(currentLesson.description);
    }

    return '';
  }, [currentLesson]);

  const handleTabChange = (val) => setTab(val);

  const handleOptIn = async () => {
    try {
      await updateUserSettings({ ...settings, optedIn: !settings.optedIn });

      // @react-ga
      ReactGA.event({
        category: 'User',
        action: 'Opted in',
      });

      notify('success', 'Updated successfully!');
      setSettings({
        ...settings,
        optedIn: !settings['optedIn'],
      });
    } catch (err) {
      notify('error', 'Failed to update: ' + (err && err.toString()));
    }
  };

  useEffect(() => {
    const optInButton = document.getElementById('opt-in-btn');
    if (optInButton) {
      optInButton.addEventListener('click', handleOptIn);
    }

    return () => {
      if (optInButton) {
        optInButton.removeEventListener('click', handleOptIn);
      }
    };
  }, [handleOptIn]);

  useEffect(() => {
    ReactGA.event({
      category: 'Content',
      action: 'Visited curriculum',
      label: curriculum ? curriculum.title : curriculumId,
    });
  }, [curriculum, curriculumId]);

  useEffect(() => {
    dispatch(fetchCourses({ curriculumId }));
  }, [curriculumId]);

  useEffect(() => {
    setShowQuizzes(false);
  }, [currentCourse]);

  useEffect(() => {
    if (
      user &&
      currentLesson &&
      curriculum &&
      curriculum.title === OPTIN_LESSON_TITLE
    ) {
      async function fetchUserSettings() {
        try {
          const res = await getUserSettings();
          setSettings(res.data.data);
        } catch (err) {
          console.log('[error.fetchusersettings]', err);
        }
      }

      fetchUserSettings();
    }
  }, [user, currentLesson.title]);

  useLayoutEffect(() => {
    if (currentLesson && currentLesson.id === typeformLessonId) {
      const iframe = document.getElementById('typeform');
      if (iframe) {
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
        <button data-tf-popup="K7ogaqj7" data-tf-iframe-props="title=CRE Career Connections Candidate" data-tf-medium="snippet" style="all:unset;font-family:Helvetica,Arial,sans-serif;display:inline-block;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;background-color:#181C34;color:#FFFFFF;font-size:20px;border-radius:5px;padding:0 33px;font-weight:bold;height:50px;cursor:pointer;line-height:50px;text-align:center;margin:0;text-decoration:none;">Create Career Profile</button><script src="//embed.typeform.com/next/embed.js"></script>
        `);
        doc.close();
      }
    }
  }, [currentLesson]);

  const handleSelectCourse = (course) => {
    const { key, label } = course;

    ReactGA.event({
      category: 'Content',
      action: 'Selected course',
      label,
    });

    let i = 0;

    for (; i < courses.length; i += 1) {
      if (key === courses[i].id) {
        break;
      }
    }

    history.replace(`/programs/${programId}/${curriculumId}/courses/${key}`);

    dispatch({ type: COURSE_SELECT, id: i });
  };
  const handleNextLesson = () => {
    if (currentCourse?.lessonAndQuizzes.length - 1 === currentLessonId) {
      let i = 0;
      for (; i < courses.length; i += 1) {
        if (courseId === courses[i].id) {
          break;
        }
      }

      if (i < courses.length - 1) {
        history.replace(
          `/programs/${programId}/${curriculumId}/courses/${courses[i + 1]}`
        );
      }
    }

    dispatch({ type: COURSE_NEXT_LESSON });
  };
  const handlePrevLesson = () => dispatch({ type: COURSE_PREV_LESSON });
  const handleSelectLesson = (id, title) => () => {
    ReactGA.event({
      category: 'Content',
      action: 'Selected lesson',
      label: title,
    });

    setShowQuizzes(false);
    dispatch({ type: COURSE_SELECT_LESSON, id });
  };
  const handleCompleteLesson = (isComplete) => async () => {
    if (isComplete) {
      ReactGA.event({
        category: 'Content',
        action: 'Completed lesson',
        label: currentLesson?.title,
      });
    }

    const _incompletedLessons = incompletedLessons.slice();
    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete });

    fetchProgram();

    let incompleteLessonsWithoutMe = incompletedLessons;
    if (isComplete) {
      let index = incompleteLessonsWithoutMe.indexOf(currentLesson._id);
      if (index !== -1) {
        incompleteLessonsWithoutMe.splice(index, 1);
      }
    } else {
      incompleteLessonsWithoutMe.push(currentLesson._id);
    }

    const checkCourse = (courses, no, incompleteLessons) => {
      const foundCourse = courses.find(course => course.no === no);
      if (!foundCourse) {
        return false;
      }
      for (const lesson of foundCourse.lessonAndQuizzes || []) {
        if (incompleteLessons.includes(lesson._id)) {
          return false;
        }
      }
      return true;
    };

    const checkComplete = (courses, todoList, incompletedLessons) => {
      return todoList.every(todo => {
        if (Array.isArray(todo)) {
          return todo.some(no => checkCourse(courses, no, incompletedLessons));
        } else {
          return checkCourse(courses, todo, incompletedLessons);
        }
      });
    };

    if (programType === 'MMCC_CANDIDATE') {
      if (checkComplete(courses, MMCC_CANDIDATE, incompleteLessonsWithoutMe)) {
        console.log('MMCC_CANDIDATE - Success');
        await createCertificate();
      }
    } else if (programType === 'AGENTE_CANDIDATE') {
      if (checkComplete(courses, AGENTE_CANDIDATE, incompleteLessonsWithoutMe)) {
        console.log('AGENTE_CANDIDATE - Success');
        await createCertificate();
      }
    } else if (isBLNext) {
      if (checkComplete(courses, BL_NEXT, incompleteLessonsWithoutMe)) {
        console.log('BL-NEXT - Success');
        await createCertificate();
      }
    } else {
      if (
        _incompletedLessons.length === 1 &&
        _incompletedLessons[0] === currentLesson._id
      ) {
        await createCertificate();
      }
    }

    if (isComplete) {
      handleNextLesson();
    }
  };
  const handleQuizSuccess = async () => {
    ReactGA.event({
      category: 'Content',
      action: 'Completed quiz',
      label: currentLesson?.title,
    });

    if (
      incompletedLessons.length === 1 &&
      incompletedLessons[0] === currentLesson._id
    ) {
      await createCertificate();
    }
    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete: true });

    fetchProgram();

    handleNextLesson();
  };
  const handleQuizFail = () => {
    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete: false });
  };
  const handleToggleForum = () => {
    setShowForum((showForum) => !showForum);
  };
  const handleToggleSidebar = () => {
    setShowSidebar((v) => !v);
  };

  const createCertificate = async () => {
    ReactGA.event({
      category: 'User',
      action: 'Completed curriculum',
      label: curriculum?.title || curriculumId,
    });

    try {
      const image = await generateImage();
      // const base64 = image.replace(/^data:image\/\w+;base64,/, "");
      // // Convert to a buffer
      // const buffer = Buffer.from(base64, "base64");

      const res = await certificateRequest({
        user: userId,
        curriculum: curriculumId,
        date: moment().format('MM-DD-yyyy'),
        image,
      });
      // console.log('[cert res]', res.data);
      if (res.data.status) {
        // await fetch(res.data.data.uploadUrl, {
        //   method: "PUT",
        //   headers: { "Content-Type": "image/png" },
        //   body: buffer,
        // });

        window.open(
          `http://${window.location.host}/certification/${res.data.data.id}`,
          '_blank'
        );
      }
    } catch (err) {
      // alert('Error happened in creating a certificate.', err);
    }
  };

  if (loading || !currentCourse || loadingCurriculums || !curriculum) {
    return (
      <div className="w-full h-full m-auto max-w-6xl flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!curriculum) {
    return (
      <Container className="w-full h-full m-auto bg-transparent flex items-start overflow-hidden relative mb-9 mt-0">
        <div className="text-center">Something went wrong.</div>
      </Container>
    );
  }

  const isCore = curriculum.isCore;

  const renderLessonQuiz = (lesson, id) => (
    <div
      className={`flex items-start justify-between items-center cursor-pointer text-sm text-${currentLesson.id === lesson.id ? 'white font-bold selected' : 'primary'
        } p-4`}
      key={lesson.id}
      onClick={handleSelectLesson(id, lesson.title)}
    >
      <div
        className={cx(
          'mr-4 w-4 h-4 check-circle border-2 border-solid rounded-full flex items-center justify-center',
          lesson.completed && 'bg-primary border-none',
          currentLesson.id === lesson.id ? 'border-white' : 'border-primary'
        )}
      >
        {lesson.completed && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" fill="white" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.25 6.91968L3 8.12952L6.75 11.759L13 5.70984L11.75 4.5L6.75 9.33936L4.25 6.91968Z"
              fill="#181C34"
            />
          </svg>
        )}
      </div>
      {showSidebar && (
        <div className="lesson-title">
          {id + 1}. {lesson.title}
        </div>
      )}
      {Array.isArray(lesson.questions) ? (
        <svg
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.24258 0C1.68562 0 1.15148 0.221249 0.757654 0.615076C0.363827 1.0089 0.142578 1.54305 0.142578 2.1V12.3C0.142578 13.46 1.08258 14.4 2.24258 14.4H7.55018C7.35018 14.088 7.18298 13.7528 7.05258 13.4H2.24258C1.95084 13.4 1.67105 13.2841 1.46476 13.0778C1.25847 12.8715 1.14258 12.5917 1.14258 12.3V2.1C1.14258 1.80826 1.25847 1.52847 1.46476 1.32218C1.67105 1.11589 1.95084 1 2.24258 1H12.4426C12.7343 1 13.0141 1.11589 13.2204 1.32218C13.4267 1.52847 13.5426 1.80826 13.5426 2.1V6.654C13.8938 6.7684 14.2286 6.9188 14.5426 7.1016V2.1C14.5426 0.94 13.6026 0 12.4426 0H2.24258Z"
            fill="black"
          />
          <path
            d="M7.55108 8.7984C7.35108 9.1104 7.18388 9.4452 7.05348 9.7984H2.24268C2.11007 9.7984 1.98289 9.74572 1.88912 9.65195C1.79535 9.55819 1.74268 9.43101 1.74268 9.2984C1.74268 9.16579 1.79535 9.03862 1.88912 8.94485C1.98289 8.85108 2.11007 8.7984 2.24268 8.7984H7.55108Z"
            fill="black"
          />
          <path
            d="M10.9417 2.09918C10.9417 1.96657 10.8891 1.8394 10.7953 1.74563C10.7015 1.65186 10.5743 1.59918 10.4417 1.59918C10.3091 1.59918 10.1819 1.65186 10.0882 1.74563C9.99441 1.8394 9.94173 1.96657 9.94173 2.09918V3.19998H8.84253C8.70992 3.19998 8.58274 3.25266 8.48898 3.34643C8.39521 3.4402 8.34253 3.56737 8.34253 3.69998C8.34253 3.83259 8.39521 3.95977 8.48898 4.05354C8.58274 4.1473 8.70992 4.19998 8.84253 4.19998H9.94173V5.29918C9.94173 5.43179 9.99441 5.55897 10.0882 5.65274C10.1819 5.7465 10.3091 5.79918 10.4417 5.79918C10.5743 5.79918 10.7015 5.7465 10.7953 5.65274C10.8891 5.55897 10.9417 5.43179 10.9417 5.29918V4.19998H12.0425C12.1751 4.19998 12.3023 4.1473 12.3961 4.05354C12.4899 3.95977 12.5425 3.83259 12.5425 3.69998C12.5425 3.56737 12.4899 3.4402 12.3961 3.34643C12.3023 3.25266 12.1751 3.19998 12.0425 3.19998H10.9417V2.09918Z"
            fill="black"
          />
          <path
            d="M6.00367 1.90841C5.96585 1.81701 5.90176 1.73889 5.81952 1.68392C5.73729 1.62895 5.64059 1.59961 5.54167 1.59961C5.44275 1.59961 5.34606 1.62895 5.26382 1.68392C5.18158 1.73889 5.1175 1.81701 5.07967 1.90841L3.18047 6.51121C3.15309 6.57229 3.13825 6.63824 3.13683 6.70515C3.13541 6.77207 3.14744 6.83859 3.17221 6.90077C3.19697 6.96295 3.23397 7.01952 3.28101 7.06714C3.32804 7.11476 3.38416 7.15244 3.44603 7.17797C3.5079 7.2035 3.57427 7.21634 3.6412 7.21574C3.70813 7.21514 3.77425 7.20111 3.83566 7.17448C3.89706 7.14785 3.9525 7.10916 3.99867 7.06071C4.04485 7.01226 4.08083 6.95503 4.10447 6.89241L4.38727 6.20721H6.69727L6.98047 6.89281C7.00477 6.9546 7.04113 7.01092 7.08743 7.0585C7.13373 7.10609 7.18904 7.14397 7.25013 7.16995C7.31123 7.19593 7.37689 7.20948 7.44328 7.20981C7.50967 7.21014 7.57546 7.19725 7.63681 7.17189C7.69816 7.14652 7.75385 7.10919 7.80062 7.06208C7.8474 7.01496 7.88432 6.959 7.90923 6.89746C7.93415 6.83593 7.94656 6.77004 7.94574 6.70366C7.94493 6.63727 7.9309 6.57172 7.90447 6.51081L6.00367 1.90841ZM6.28407 5.20721H4.80007L5.54167 3.40961L6.28407 5.20721Z"
            fill="black"
          />
          <path
            d="M1.74268 11.7C1.74268 11.424 1.96668 11.2 2.24268 11.2H5.44588C5.57848 11.2 5.70566 11.2527 5.79943 11.3465C5.8932 11.4402 5.94588 11.5674 5.94588 11.7C5.94588 11.8326 5.8932 11.9598 5.79943 12.0536C5.70566 12.1473 5.57848 12.2 5.44588 12.2H2.24268C1.96668 12.2 1.74268 11.976 1.74268 11.7Z"
            fill="black"
          />
          <path
            d="M16.3327 11.6C16.3327 14.03 14.3627 16 11.9327 16C9.50271 16 7.53271 14.03 7.53271 11.6C7.53271 9.17001 9.50271 7.20001 11.9327 7.20001C14.3627 7.20001 16.3327 9.17001 16.3327 11.6ZM12.3327 8.80001C12.3327 8.69393 12.2906 8.59218 12.2156 8.51717C12.1405 8.44215 12.0388 8.40001 11.9327 8.40001C11.8266 8.40001 11.7249 8.44215 11.6499 8.51717C11.5749 8.59218 11.5327 8.69393 11.5327 8.80001V11.2H9.13271C9.02663 11.2 8.92489 11.2422 8.84987 11.3172C8.77486 11.3922 8.73271 11.4939 8.73271 11.6C8.73271 11.7061 8.77486 11.8078 8.84987 11.8829C8.92489 11.9579 9.02663 12 9.13271 12H11.5327V14.4C11.5327 14.5061 11.5749 14.6078 11.6499 14.6829C11.7249 14.7579 11.8266 14.8 11.9327 14.8C12.0388 14.8 12.1405 14.7579 12.2156 14.6829C12.2906 14.6078 12.3327 14.5061 12.3327 14.4V12H14.7327C14.8388 12 14.9405 11.9579 15.0156 11.8829C15.0906 11.8078 15.1327 11.7061 15.1327 11.6C15.1327 11.4939 15.0906 11.3922 15.0156 11.3172C14.9405 11.2422 14.8388 11.2 14.7327 11.2H12.3327V8.80001Z"
            fill="black"
          />
        </svg>
      ) : lesson.video !== '' ? (
        <svg
          width="18"
          height="11"
          viewBox="0 0 18 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.7721 1.54669C17.7027 1.50751 17.6241 1.48677 17.5439 1.48652C17.4637 1.48627 17.3849 1.50653 17.3153 1.54528L13.7288 3.54237V2.82432C13.7279 2.07553 13.4223 1.35764 12.879 0.82816C12.3356 0.298679 11.5989 0.000845889 10.8305 0H1.0678C0.784698 0.000314743 0.513288 0.110044 0.313107 0.305115C0.112926 0.500186 0.000322987 0.764669 0 1.04054V8.17568C0.000868046 8.92447 0.306503 9.64236 0.849853 10.1718C1.3932 10.7013 2.12989 10.9992 2.8983 11H12.661C12.9441 10.9997 13.2155 10.89 13.4157 10.6949C13.6159 10.4998 13.7285 10.2353 13.7288 9.95946V7.45755L17.3153 9.45472C17.3849 9.49348 17.4637 9.51374 17.5439 9.51348C17.624 9.51323 17.7027 9.49246 17.772 9.45325C17.8414 9.41405 17.8989 9.35779 17.9389 9.2901C17.9789 9.22242 18 9.14568 18 9.06757V1.93243C18 1.85432 17.979 1.77757 17.939 1.70987C17.899 1.64217 17.8414 1.5859 17.7721 1.54669ZM12.8136 9.95946C12.8135 9.99887 12.7974 10.0366 12.7688 10.0645C12.7402 10.0924 12.7015 10.108 12.661 10.1081H2.8983C2.37255 10.1075 1.86849 9.90376 1.49672 9.54148C1.12496 9.1792 0.91584 8.68801 0.915254 8.17568V1.04054C0.915315 1.00113 0.931405 0.963359 0.96 0.935495C0.988594 0.907631 1.02736 0.891951 1.0678 0.891892H10.8305C11.3563 0.892462 11.8603 1.09624 12.2321 1.45852C12.6039 1.8208 12.813 2.31199 12.8136 2.82432V9.95946ZM17.0847 8.29913L13.7288 6.43047V4.56946L17.0847 2.7008V8.29913Z"
            fill="black"
          />
        </svg>
      ) : (
        <svg
          width="14"
          height="16"
          viewBox="0 0 14 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.3999 2.4V13.6C1.3999 13.9713 1.5474 14.3274 1.80995 14.5899C2.0725 14.8525 2.4286 15 2.7999 15H11.1999C11.5712 15 11.9273 14.8525 12.1899 14.5899C12.4524 14.3274 12.5999 13.9713 12.5999 13.6V5.4394C12.5999 5.25289 12.5626 5.06827 12.4902 4.89637C12.4179 4.72447 12.3119 4.56877 12.1785 4.4384L9.0705 1.399C8.80896 1.14326 8.4577 1.00005 8.0919 1H2.7999C2.4286 1 2.0725 1.1475 1.80995 1.41005C1.5474 1.6726 1.3999 2.0287 1.3999 2.4V2.4Z"
            stroke="#181C34"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.3999 1V3.8C8.3999 4.1713 8.5474 4.5274 8.80995 4.78995C9.0725 5.0525 9.4286 5.2 9.7999 5.2H12.5999"
            stroke="#181C34"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  const renderCompleteButton = () => (
    <Button
      className="font-semibold px-4 py-2 text-base complete-btn bg-light-yellow rounded-lg text-white font-bold"
      caption={
        currentLesson.completed ? (
          <div className="flex items-center">
            Complete
            <img className="h-2 ml-2" src={checkIco} alt="done" />
          </div>
        ) : (
          'Mark Complete'
        )
      }
      type={currentLesson.completed ? 'secondary' : 'misc'}
      onClick={handleCompleteLesson(!currentLesson.completed)}
    />
  );

  const renderOptInButton = () => (
    <Button
      className="font-semibold px-4 py-2 text-base complete-btn bg-light-yellow rounded-lg text-white font-bold"
      caption={
        settings.optedIn ? (
          <div className="flex items-center">
            Added
            <img className="h-2 ml-2" src={checkIco} alt="done" />
          </div>
        ) : (
          'Opt In'
        )
      }
      type={settings.optedIn ? 'secondary' : 'misc'}
      onClick={handleOptIn}
    />
  );

  const isMMCC = programType === 'MMCC_CANDIDATE';
  const isAgent = programType === 'AGENTE_CANDIDATE';

  return (
    <Container className="w-full h-full m-auto bg-transparent flex items-start overflow-hidden relative mb-9 mt-0">
      <div
        id="drawContainer"
        className={isMMCC ? 'mmcc' : isAgent ? 'agent' : isCore ? isIcsc ? 'icsc' : isBLNext ? 'bl-next' : 'curriculum' : isBLNext ? 'bl-next' : 'endorsement'}
      >
        <div className="content-container">
          <div className="name">{user?.name}</div>
          {isMMI && <div className="small-name">{user?.name}</div>}
          {!isCore && !isMMI && <div className="curriculum">{curriculum.title}</div>}
          {!isCore && !isMMI && <div className="sign">Spencer Burton</div>}
          <div className="date">{moment().format('MMM DD, YYYY')}</div>
        </div>
      </div>
      {/* start left side */}
      <SidebarContainer
        className="mr-9 bg-transparent flex"
        on={showSidebar}
        bg={primaryColor}
      >
        <div className="w-full bg-transparent flex flex-col">
          <TitleContainer
            className={`px-${showSidebar ? 6 : 2} py-${showSidebar ? 9 : 2
              } mb-5 bg-primary text-yellow w-full flex justify-between items-center border border-solid border-white`}
            bg={primaryColor}
            isIcsc={isIcsc}
          >
            {showSidebar && (
              <div className="font-bold text-white text-2xl tracking-widest uppercase">
                {curriculum.title}
              </div>
            )}
            <div className="progress-bar">
              <CircularProgressbar
                value={percent}
                text={''}
                strokeWidth={5}
                styles={buildStyles({
                  textColor: '#fff',
                  textSize: '16px',
                  pathColor: '#fff',
                  trailColor: 'rgba(255, 255, 255, 0.1)',
                })}
              />
              <img
                className="logo"
                src={isIcsc ? icscProgressIcon : logo}
                alt="logo"
              />
            </div>
          </TitleContainer>
          <ContentSelector
            className={`bg-white px-${showSidebar ? 6 : 2} py-${showSidebar ? 9 : 0
              }`}
          >
            {showSidebar && (
              <div className="course-select-box">
                <div className="text-black text-lg mb-1 font-bold">
                  Course Selection:
                </div>
                <Select
                  className="border border-solid border-black rounded-lg"
                  value={{
                    key: currentCourse.id,
                    label: currentCourse.title.toUpperCase(),
                  }}
                  options={courses.map(({ id, title }, index) => ({
                    key: id,
                    label: title.toUpperCase(),
                  }))}
                  placeholder="Please select course"
                  onChange={handleSelectCourse}
                />
              </div>
            )}
            <div className="lesson-list py-4 text-white flex-grow">
              {showSidebar && (
                <div className="text-xl font-semibold text-primary mb-4">
                  Lessons
                </div>
              )}
              {currentCourse?.lessonAndQuizzes.map((lesson, id) =>
                renderLessonQuiz(lesson, id)
              )}
            </div>
          </ContentSelector>
        </div>
        <button
          className="toggle-btn bg-light-yellow"
          onClick={handleToggleSidebar}
        >
          {showSidebar ? (
            <svg
              width="18"
              height="12"
              viewBox="0 0 18 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.1634 6.3607L10.165 6.00661C10.165 4.53497 10.0788 3.19332 9.94897 2.31871L9.83524 1.77483C9.77197 1.48678 9.68879 1.1588 9.60207 0.991371C9.28461 0.378924 8.66389 0 7.99954 0H7.94172C7.50864 0.0143198 6.59883 0.394346 6.59883 0.407564C5.13485 1.02183 2.31046 2.87572 1.006 4.19739L0.626928 4.59419C0.527664 4.70173 0.41614 4.82901 0.346905 4.92822C0.115635 5.23444 0 5.61337 0 5.99229C0 6.41527 0.129817 6.80852 0.375269 7.13016L0.764727 7.55047L0.85199 7.64017C2.03561 8.92344 5.12612 11.0218 6.74283 11.664L6.98692 11.7576C7.28064 11.8629 7.69218 11.9884 7.94172 12C8.25917 12 8.56244 11.9262 8.85153 11.7808C9.21262 11.577 9.50061 11.2554 9.65988 10.8764C9.76134 10.6143 9.92061 9.82669 9.92061 9.81237C10.0666 9.01826 10.1513 7.76518 10.1634 6.3607ZM18 5.99956C18 5.1613 17.3269 4.48166 16.4967 4.48166L12.7975 4.80881C12.1463 4.80881 11.6183 5.34195 11.6183 5.99956C11.6183 6.65827 12.1463 7.19031 12.7975 7.19031L16.4967 7.51746C17.3269 7.51746 18 6.83782 18 5.99956Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg
              width="18"
              height="12"
              viewBox="0 0 18 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.83656 6.3607L7.83503 6.00661C7.83503 4.53497 7.92121 3.19332 8.05103 2.31871L8.16476 1.77483C8.22803 1.48678 8.31121 1.1588 8.39793 0.991371C8.71539 0.378924 9.33611 0 10.0005 0H10.0583C10.4914 0.0143198 11.4012 0.394346 11.4012 0.407564C12.8652 1.02183 15.6895 2.87572 16.994 4.19739L17.3731 4.59419C17.4723 4.70173 17.5839 4.82901 17.6531 4.92822C17.8844 5.23444 18 5.61337 18 5.99229C18 6.41527 17.8702 6.80852 17.6247 7.13016L17.2353 7.55047L17.148 7.64017C15.9644 8.92344 12.8739 11.0218 11.2572 11.664L11.0131 11.7576C10.7194 11.8629 10.3078 11.9884 10.0583 12C9.74083 12 9.43756 11.9262 9.14847 11.7808C8.78738 11.577 8.49939 11.2554 8.34012 10.8764C8.23866 10.6143 8.07939 9.82669 8.07939 9.81237C7.93338 9.01826 7.84872 7.76518 7.83656 6.3607ZM0 5.99956C0 5.1613 0.673083 4.48166 1.50325 4.48166L5.20248 4.80881C5.85375 4.80881 6.38174 5.34195 6.38174 5.99956C6.38174 6.65827 5.85375 7.19031 5.20248 7.19031L1.50325 7.51746C0.673083 7.51746 0 6.83782 0 5.99956Z"
                fill="white"
              />
            </svg>
          )}
          <div className="callout">{showSidebar ? 'Collapse' : 'Expand'}</div>
        </button>
      </SidebarContainer>

      {/* start content */}
      <div className="flex-grow bg-white rounded-12 overflow-auto">
        <>
          <ContentTitleContainer className="flex items-end justify-between">
            <div className="flex flex-col w-full">
              <div className="text-primary font-semibold text-20 uppercase flex items-center justify-between">
                {currentCourse.title}
              </div>
              <div className="text-primary font-bold text-lg mt-10">
                Lesson {currentLessonId + 1}. {currentLesson.title}
              </div>
            </div>
          </ContentTitleContainer>

          {!showForum && (
            <>
              {Array.isArray(currentLesson.questions) ? (
                <div className="p-9" key={currentLesson.id}>
                  <Quizzes
                    description={currentLesson.description}
                    quizId={currentLesson._id}
                    quizzes={currentLesson.questions}
                    onNext={handleNextLesson}
                    onSuccess={handleQuizSuccess}
                    onFail={handleQuizFail}
                  />
                </div>
              ) : (
                <>
                  {/* video wrapper */}
                  {/* <div className="w-full my-9 px-6">
                    <div
                      dangerouslySetInnerHTML={{ __html: currentLesson.video }}
                    />
                  </div> */}
                  <div className="w-full rounded-lg px-9">
                    {currentLesson.image && (
                      <img
                        className="w-full rounded-lg "
                        src={currentLesson.image}
                        alt="video_img"
                      />
                    )}
                    {currentLesson.video && (
                      <div
                        className="w-full rounded-lg "
                        dangerouslySetInnerHTML={{
                          __html:
                            currentLesson.video.indexOf('[video id=') === -1
                              ? currentLesson.video
                              : replaceVideoScripts(currentLesson.video),
                        }}
                      />
                    )}
                  </div>

                  <div className="my-9 px-9 w-full">
                    {!Array.isArray(currentLesson.questions) && (
                      <div className="mt-4 flex justify-end">
                        {renderCompleteButton()}
                      </div>
                    )}
                    <Tabs
                      options={tabOptions}
                      current={tab}
                      onChange={handleTabChange}
                    />
                    {/* content wrapper */}
                    {tab === 'lesson' && (
                      <div className="w-full html-content">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: replaceOptInButton(
                              lessonContent,
                              settings.optedIn
                            ),
                          }}
                        />
                        {currentLesson.id === typeformLessonId && (
                          <iframe id="typeform" width="100%" height="1000px" />
                        )}
                      </div>
                    )}
                    {/* {tab === 'resources' &&
                      currentLesson.resources &&
                      currentLesson.resources.length > 0 && (
                        <ResourceContainer>
                          <div>
                            {currentLesson.resources.map((resource, id) => (
                              <div
                                key={resource.title + id}
                                className="mb-2 flex items-center justify-between"
                              >
                                {resource.title}
                                <a
                                  href={resource.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-dark-blue whitespace-nowrap underline"
                                >
                                  <img
                                    className="w-4 h-4"
                                    src={downloadIco}
                                    alt="download"
                                  />
                                </a>
                              </div>
                            ))}
                          </div>
                        </ResourceContainer>
                      )}
                    {tab === 'resources' &&
                      (!currentLesson.resources ||
                        (currentLesson.resources &&
                          currentLesson.resources.length === 0)) && (
                        <div className="w-full h-32 flex items-center justify-center text-base">
                          No resources.
                        </div>
                      )} */}
                    {tab === 'q&a' && <Forum forumId={currentCourse.id} />}
                  </div>
                </>
              )}
            </>
          )}
        </>
      </div>
    </Container>
  );
};

export default Courses;
